/*
 * Lightweight network-status helper.
 *
 * Why not just use `navigator.onLine`?
 *   - Phones routinely lie about connectivity. A device on a captive-portal
 *     WiFi can be "online" but unable to reach the server. A device on
 *     cellular-only mode after disabling WiFi often still reports
 *     navigator.onLine === true.
 *   - When a request fails with a network error, that's the most reliable
 *     signal we have that "this user is, for our purposes, offline right
 *     now". This helper lets the rest of the app remember that and treat
 *     the next ~30 seconds as offline without waiting for OS-level events.
 *
 * The helper exposes a single `isLikelyOffline()` that returns true when:
 *   • `navigator.onLine === false`, OR
 *   • A network failure was observed within the last STICKY_WINDOW ms.
 */

const STICKY_WINDOW_MS = 20_000;
let lastNetworkFailureAt = 0;

export const markNetworkFailure = () => {
  lastNetworkFailureAt = Date.now();
};

export const markNetworkSuccess = () => {
  // A successful request clears the sticky offline flag immediately.
  const wasMarkedOffline = lastNetworkFailureAt > 0;
  lastNetworkFailureAt = 0;
  // Notify listeners that we just transitioned from offline → online.
  if (wasMarkedOffline) {
    recoveryListeners.forEach((cb) => {
      try { cb(); } catch (err) { console.error('Recovery listener threw:', err); }
    });
  }
};

const recoveryListeners = new Set();
export const onNetworkRecovered = (cb) => {
  recoveryListeners.add(cb);
  return () => recoveryListeners.delete(cb);
};

export const isLikelyOffline = () => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (lastNetworkFailureAt && Date.now() - lastNetworkFailureAt < STICKY_WINDOW_MS) return true;
  return false;
};

export const isNetworkError = (err) => {
  if (!err) return false;
  // Axios shape: no response means the request never reached the server.
  if (err.response) return false;
  if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') return true;
  if (err.message === 'Network Error') return true;
  // Plain fetch / TypeError: "Failed to fetch" / abort
  if (err.message && /failed to fetch|networkerror|load failed|aborted/i.test(err.message)) return true;
  return false;
};
